import React from 'react';
import { Box, Card, CardContent, Typography, Skeleton, Stack, Chip } from '@mui/material';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { PresentationOutcomeReport } from '../../../types/reporting';

interface PresentationOutcomeChartProps {
  data?: PresentationOutcomeReport;
  loading?: boolean;
}

const OUTCOME_COLORS: Record<string, string> = {
  won: '#4CAF50',
  lost: '#F44336',
  pending: '#FF9800',
  followup: '#2196F3',
  'follow-up': '#2196F3',
};

const FALLBACK_COLORS = ['#9C27B0', '#00BCD4', '#795548', '#607D8B', '#E91E63'];

const PresentationOutcomeChart: React.FC<PresentationOutcomeChartProps> = ({
  data,
  loading = false,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width="40%" height={28} />
          <Skeleton variant="circular" width={200} height={200} sx={{ mt: 2, mx: 'auto' }} />
        </CardContent>
      </Card>
    );
  }

  const outcomes = data?.outcomes ?? [];
  const chartData = outcomes.map((o, i) => ({
    name: o.outcome,
    value: o.jobCount,
    percentage: o.percentage,
    color:
      OUTCOME_COLORS[o.outcome.toLowerCase()] ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length],
  }));

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Presentation Outcomes</Typography>
          {data && (
            <Chip
              size="small"
              label={`${data.jobsWithOutcome} of ${data.totalJobs} jobs`}
              variant="outlined"
            />
          )}
        </Stack>
        <Box sx={{ width: '100%', height: 300 }}>
          {chartData.length > 0 ? (
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 4,
                  }}
                  formatter={(value: number, _name: string, entry: any) => [
                    `${value} jobs (${entry.payload.percentage}%)`,
                    entry.payload.name,
                  ]}
                />
                <Legend layout="vertical" align="right" verticalAlign="middle" />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Typography color="text.secondary">No presentation data in this period</Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PresentationOutcomeChart;
